/**
 * TaskCard: Compact, beautiful Kanban card for the task board.
 *
 * Design reference: Linear/Plane/Shortcut — information-dense, clean, no clutter.
 * Uses the Cyber-Noir design token palette (gold/lime accents, dark surfaces).
 *
 * Draggable via @dnd-kit. The entire card is the drag handle.
 * Click opens the detail panel; drag moves between columns.
 */

import { forwardRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  Hourglass,
  Shield,
  Link2,
  Star,
  User,
  Bot,
  AlertCircle,
  CalendarClock,
  RefreshCcw,
  CalendarPlus,
  GitPullRequest,
  Workflow,
} from 'lucide-react';
import type { TaskItem, TaskPriority } from '@/api/types';
import { agentCompletionPolicy, getWorkflowStage, hasTag } from '@/lib/agentWorkflow';
import { pickCardDate, type TaskDateKind } from '@/lib/taskDates';
import { getTaskOwner } from '@/lib/taskOwner';

// ── Priority visual config ──────────────────────────────
const PRIORITY_CONFIG: Record<TaskPriority, { color: string; bg: string; label: string }> = {
  critical: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)', label: 'CRIT' },
  high:     { color: '#f97316', bg: 'rgba(249, 115, 22, 0.12)', label: 'HIGH' },
  medium:   { color: '#eab308', bg: 'rgba(234, 179, 8, 0.10)', label: 'MED' },
  low:      { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.10)', label: 'LOW' },
};

// ── Governor status icon mapping ────────────────────────
const GOVERNOR_VISUAL: Record<string, { Icon: typeof Shield; color: string; title: string }> = {
  passed:           { Icon: ShieldCheck, color: 'text-green-400', title: 'Governor: passed' },
  failed:           { Icon: ShieldX, color: 'text-red-400', title: 'Governor: failed' },
  held_for_review:  { Icon: ShieldAlert, color: 'text-yellow-300', title: 'Governor: held for review' },
  pending:          { Icon: Hourglass, color: 'text-gray-500', title: 'Governor: pending' },
  director_override: { Icon: Shield, color: 'text-blue-400', title: 'Director override' },
};

// ── Room color accents (matches TasksTab ROOMS) ─────────
const ROOM_COLOR: Record<string, string> = {
  sakme: '#a78bfa',
  seitiate: '#34d399',
  closedclaw: '#f87171',
  skills: '#fbbf24',
  launch: '#22d3ee',
  solopreneurs: '#60a5fa',
  cross: '#94a3b8',
  decisions: '#c084fc',
};

function getRoomSlug(category: string | null): string | null {
  if (!category) return null;
  const prefix = category.split('/')[0];
  if (prefix === 'sakme' || category.startsWith('sakme/')) return 'sakme';
  if (prefix === 'seitiate' || category.startsWith('seitiate/')) return 'seitiate';
  if (prefix === 'closedclaw' || category.startsWith('closedclaw/')) return 'closedclaw';
  if (prefix === 'skills' || category.startsWith('skills/')) return 'skills';
  if (prefix === 'launch' || category.startsWith('launch/')) return 'launch';
  if (prefix === 'solopreneurs' || category.startsWith('solopreneurs/')) return 'solopreneurs';
  return null;
}

// ── Date chip visuals ────────────────────────────────────
//
// BOARD-DATE-VISIBILITY-1: every card shows one date chip, prefer due_date.
// `created_at` / `updated_at` are metadata — labelled accordingly so the
// reader can never confuse them with scheduled work.
const DATE_ICON: Record<TaskDateKind, typeof CalendarClock> = {
  due: CalendarClock,
  updated: RefreshCcw,
  created: CalendarPlus,
};
const DATE_TONE: Record<TaskDateKind, { color: string; bg: string }> = {
  due:     { color: '#9ca3af', bg: 'rgba(148, 163, 184, 0.10)' },
  updated: { color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.08)' },
  created: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.08)' },
};

// ── Inner card (also used as drag overlay) ──────────────

export interface TaskCardInnerProps {
  task: TaskItem;
  isDragging?: boolean;
  isOverlay?: boolean;
}

export const TaskCardInner = forwardRef<HTMLDivElement, TaskCardInnerProps & React.HTMLAttributes<HTMLDivElement>>(
  function TaskCardInner({ task, isDragging = false, isOverlay = false, style, className, ...rest }, ref) {
    const priority = PRIORITY_CONFIG[task.priority];
    const govStatus = task.governor_status ? GOVERNOR_VISUAL[task.governor_status] : null;
    const isTopTwelve = Array.isArray(task.tags) && task.tags.includes('top-12');
    const roomSlug = getRoomSlug(task.category);
    const roomColor = roomSlug ? ROOM_COLOR[roomSlug] : null;
    const hasDependencies = (task.depends_on?.length ?? 0) > 0;
    const categoryLabel = task.category?.split('/').pop() ?? task.category;
    const owner = getTaskOwner(task);
    const agentWorking = owner.kind === 'agent' && task.status === 'in_progress';
    const isStale = task.status === 'in_progress' && task.updated_at
      && (Date.now() - new Date(task.updated_at).getTime()) > 24 * 60 * 60 * 1000;
    const showActor = task.last_actor_display && task.last_actor_display !== task.assigned_to;
    const cardDate = pickCardDate(task);
    const DateIcon = cardDate ? DATE_ICON[cardDate.kind] : null;
    const dateTone = cardDate ? DATE_TONE[cardDate.kind] : null;
    const workflow = getWorkflowStage(task);
    const completionPolicy = agentCompletionPolicy(task);
    const showPrState = workflow.stage?.key === 'pr-ready' || hasTag(task, 'evidence:pr');

    return (
      <div
        ref={ref}
        style={style}
        className={`
          group relative rounded-lg border transition-all duration-150
          ${isDragging
            ? 'border-[var(--sp-accent)]/60 shadow-lg shadow-[var(--sp-accent)]/10 scale-[1.02] z-50'
            : 'border-[var(--sp-border)] hover:border-[#334155] hover:shadow-md hover:shadow-black/20'
          }
          ${isOverlay ? 'opacity-95 rotate-[1.5deg]' : ''}
          ${isStale ? 'opacity-50' : ''}
          bg-[var(--theme-surface-secondary)] cursor-grab active:cursor-grabbing
          ${className ?? ''}
        `}
        {...rest}
      >
        {/* Priority accent bar (left edge) */}
        <div
          className="absolute top-1.5 bottom-1.5 left-0 w-[3px] rounded-full"
          style={{ backgroundColor: priority.color, opacity: 0.7 }}
        />

        <div className="pl-3.5 pr-3 pt-2.5 pb-2.5">
          {/* Row 1: Task ID + Priority + Top-12 star */}
          <div className="flex items-center gap-1.5 mb-1.5">
            {isTopTwelve && (
              <Star className="w-3 h-3 text-yellow-300 shrink-0" fill="currentColor" />
            )}
            {task.task_id && (
              <span
                className="text-[10px] font-mono font-bold tracking-wide shrink-0"
                style={{ color: priority.color }}
              >
                {task.task_id}
              </span>
            )}
            <span
              className="text-[9px] font-bold font-mono uppercase px-1.5 py-px rounded ml-auto shrink-0"
              style={{ color: priority.color, backgroundColor: priority.bg }}
            >
              {priority.label}
            </span>
          </div>

          {/* Row 2: Title (2 lines max) */}
          <p className="text-[13px] leading-[1.35] font-medium text-gray-200 line-clamp-2 mb-2">
            {task.title}
          </p>

          {/* Row 3: Metadata chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Owner identity — chip shows agent (bot) vs human (user) icon
                derived from the assignee handle, NOT from last_actor_type
                (the most-recent editor is often a different actor than the
                row's owner — BOARD-TEAM-OWNERSHIP-UX-1). */}
            {owner.kind !== 'unassigned' && (
              <span
                data-testid="task-card-owner-chip"
                data-owner-kind={owner.kind}
                title={owner.kind === 'agent' ? `Agent: ${owner.handle}` : `Assigned to ${owner.handle}`}
                className="inline-flex items-center gap-1 text-[10px] text-gray-400 bg-gray-800/50 rounded px-1.5 py-0.5"
              >
                {owner.kind === 'agent'
                  ? <Bot className="w-2.5 h-2.5 text-[#39FF14]/60" />
                  : <User className="w-2.5 h-2.5" />
                }
                {owner.handle}
                {/* Status dot: green=active agent, gray=idle */}
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${agentWorking ? 'bg-green-400' : 'bg-gray-600'}`}
                  title={agentWorking ? 'Active' : 'Idle'}
                />
              </span>
            )}

            {/* Room/category tag */}
            {categoryLabel && (
              <span
                className="text-[10px] rounded px-1.5 py-0.5"
                style={{
                  color: roomColor ?? '#6b7280',
                  backgroundColor: `${roomColor ?? '#6b7280'}15`,
                }}
              >
                {categoryLabel}
              </span>
            )}

            {/* Agent-managed lifecycle stage. If multiple workflow:* tags are
                present, getWorkflowStage returns the most advanced stage and
                marks the conflict for cleanup visibility. */}
            {workflow.stage && (
              <span
                data-testid="task-card-workflow-chip"
                className="inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5"
                style={{ color: workflow.stage.color, backgroundColor: workflow.stage.bg }}
                title={workflow.hasConflict ? `Multiple workflow tags: ${workflow.workflowTags.join(', ')}` : workflow.stage.label}
              >
                <Workflow className="w-2.5 h-2.5" />
                {workflow.stage.shortLabel}
                {workflow.hasConflict ? ' !' : ''}
              </span>
            )}

            {completionPolicy === 'blocked' && (
              <span
                className="text-[10px] rounded px-1.5 py-0.5 text-rose-300 bg-rose-950/30"
                title="Agent cannot complete this task; human review is required"
              >
                human gate
              </span>
            )}

            {showPrState && (
              <span
                data-testid="task-card-pr-chip"
                className="inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 text-blue-300 bg-blue-950/30"
                title={task.tests_status ? `PR/evidence state; tests: ${task.tests_status}` : 'PR/evidence state'}
              >
                <GitPullRequest className="w-2.5 h-2.5" />
                {task.tests_status ? `PR ${task.tests_status}` : 'PR'}
              </span>
            )}

            {/* Dependency indicator */}
            {hasDependencies && (
              <span className="inline-flex items-center text-[10px] text-orange-400/70" title={`Depends on ${task.depends_on.length} task(s)`}>
                <Link2 className="w-2.5 h-2.5" />
              </span>
            )}

            {/* Date chip — Due/Updated/Created (BOARD-DATE-VISIBILITY-1) */}
            {cardDate && DateIcon && dateTone && (
              <span
                data-testid="task-card-date-chip"
                data-date-kind={cardDate.kind}
                className={`inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 ${cardDate.isOverdue ? 'font-semibold' : ''}`}
                style={{
                  color: cardDate.isOverdue ? '#ef4444' : dateTone.color,
                  backgroundColor: cardDate.isOverdue ? 'rgba(239, 68, 68, 0.12)' : dateTone.bg,
                }}
                title={`${cardDate.label} (${new Date(cardDate.iso).toLocaleString()})`}
              >
                <DateIcon className="w-2.5 h-2.5" />
                {cardDate.label}
              </span>
            )}

            {/* Spacer to push governor to the right */}
            <span className="flex-1" />

            {/* Governor status */}
            {govStatus && (
              <span className={`${govStatus.color} shrink-0`} title={govStatus.title}>
                <govStatus.Icon className="w-3.5 h-3.5" />
              </span>
            )}

            {/* Stale indicator (>24h no activity on active tasks — dims the card) */}
            {task.status === 'in_progress' && task.updated_at && (
              (() => {
                const staleMs = Date.now() - new Date(task.updated_at).getTime();
                if (staleMs > 24 * 60 * 60 * 1000) {
                  return (
                    <span className="text-orange-400/70 shrink-0" title="Stale: no activity in 24+ hours">
                      <AlertCircle className="w-3 h-3" />
                    </span>
                  );
                }
                return null;
              })()
            )}
          </div>

          {/* Row 4: Actor identity (who last touched this) */}
          {showActor && (
            <div className="flex items-center gap-1 mt-1.5 text-[10px] text-gray-500">
              {task.last_actor_type === 'agent'
                ? <Bot className="w-2.5 h-2.5 text-[#39FF14]/60" />
                : <User className="w-2.5 h-2.5" />
              }
              <span className="truncate">Updated by {task.last_actor_display}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

// ── Sortable wrapper (used inside Kanban columns) ───────

interface SortableTaskCardProps {
  task: TaskItem;
  onClick: (task: TaskItem) => void;
}

export function SortableTaskCard({ task, onClick }: SortableTaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, data: { task } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: isDragging ? 0.4 : 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.15 }}
    >
      <TaskCardInner
        ref={setNodeRef}
        task={task}
        isDragging={isDragging}
        style={style}
        onClick={(e) => {
          // Only fire click if not dragging
          if (!isDragging) {
            e.stopPropagation();
            onClick(task);
          }
        }}
        {...attributes}
        {...listeners}
      />
    </motion.div>
  );
}
